import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState, useMemo, Fragment } from 'react';
import { useFuzzySearchList } from '@nozbe/microfuzz/react';
import {
  FloatingList,
  useFloating,
  useInteractions,
  useListItem,
  useListNavigation,
} from '@floating-ui/react';

import { useClientRect } from '@/lib/hooks/use-client-rect';
import { Icon } from '@/components/icon';
import { Overlay } from '@/components/overlay';

import { Tooltip } from '@/components/tooltip';

import {
  FormInputSize,
  SelectOptions,
  isOptionGroup,
  SelectOption,
  Option,
  OptionValue,
  flattenSelectOptions,
  padSelectOptions,
  toggleSelectOptions,
  OptionGroup,
  getSelectedOption,
  filterOptions,
} from '../types';
import { Input } from '../input';

import styles from './search-input.module.scss';

const makeOptionKey = <T extends OptionValue>(value: T, group?: OptionGroup<T>): string =>
  `${group?.key ?? group?.label ?? ''}\0${String(value)}`;

type KeyedOption<T extends OptionValue> = Option<T> & { _key: string };

const findGroupForOption = <T extends OptionValue>(
  options: SelectOptions<T>,
  target: Option<T>,
): OptionGroup<T> | null => {
  return (
    options.filter(isOptionGroup).find((g) => g.options.some((o) => o.value === target.value)) ??
    null
  );
};

const getSelectedOptionLabel = <OV extends OptionValue>(
  value: SelectOptions<OV>,
  multiple: boolean,
) => {
  const single = value.at(0);
  if (single === undefined) {
    return undefined;
  }
  if (multiple) {
    return flattenSelectOptions(value)
      .map((o) => o.label)
      .join(', ');
  }
  return isOptionGroup(single) ? `${single.options.at(0)?.label} (${single.label})` : single.label;
};

interface BaseSearchInputProps<O extends OptionValue = string> {
  options: SelectOption<O>[];
  autoFocus?: boolean;
  className?: classNames.Argument;
  size?: FormInputSize;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  label?: string;
  showShortcuts?: boolean;
  fullWidth?: boolean;
  allowCustomValue?: boolean;
  theme?: 'light';
  customValueHint?: string;
  customValueLabel?: string;
  closeOnSelect?: boolean;
}

/**
 * Search input for single value selection.
 * - Output is the selected option object.
 * - If a selected option is within a group, it is outputted as a nested object within the group.
 * - Input value is a single option that should be nested in its parent group if applicable.
 * - Options are assumed to have unique keys within their parent group.
 *
 * Use when you need to pick a single item from a list of options that may be organized into groups.
 */

interface SingleSearchInputProps<
  OV extends OptionValue,
  O extends Option<OV> | Readonly<Option<OV>> = Option<OV>,
  OG extends OptionGroup<OV> | Readonly<OptionGroup<OV>> = OptionGroup<OV>,
> extends BaseSearchInputProps<OV> {
  value?: SelectOption<OV>;
  onChange: (option: O, group?: OG) => void;
}

export const SingleSearchInput = <
  OV extends OptionValue,
  O extends Option<OV> | Readonly<Option<OV>> = Option<OV>,
  OG extends OptionGroup<OV> | Readonly<OptionGroup<OV>> = OptionGroup<OV>,
>(
  props: SingleSearchInputProps<OV, O, OG>,
) => {
  const { value, onChange, ...rest } = props;
  return (
    <SearchInput
      {...rest}
      value={value}
      onChange={(optionsOrGroups) => {
        const [option, group] = getSelectedOption(optionsOrGroups);
        onChange(option as O, group as OG);
      }}
    />
  );
};

/**
 * Search input for multi value selection.
 * - Output is an array of the selected option objects.
 * - If a selected option is within a group, it is outputted as a nested object within the group.
 * - Input value is a filtered array of options/option groups (options can have duplicate values in different groups)
 *
 * Use when you need to pick multiple items from a list of options that may be organized into groups.
 */

interface MultiSearchInputProps<
  OV extends OptionValue,
  O extends Option<OV> = Option<OV>,
  OG extends OptionGroup<OV> = OptionGroup<OV>,
> extends BaseSearchInputProps<OV> {
  value: (O | OG)[];
  onChange: (options: (O | OG)[]) => void;
}

export const MultiSearchInput = <
  OV extends OptionValue,
  O extends Option<OV> = Option<OV>,
  OG extends OptionGroup<OV> = OptionGroup<OV>,
>(
  props: MultiSearchInputProps<OV, O, OG>,
) => {
  const { value, onChange, ...rest } = props;
  return (
    <SearchInput
      {...rest}
      value={value}
      onChange={(optionsOrGroups) => onChange(optionsOrGroups as (O | OG)[])}
    />
  );
};

/**
 * Search input for single value selection.
 * - Output is the selected option and its group (if any).
 * - Input value is the value of the selected option (options are assumed to have UNIQUE values across all groups)
 *
 * Use when you need to pick a single item from a flat list of unique options.
 */

interface SingleValueSearchInputProps<
  OV extends OptionValue,
  O extends Option<OV> = Option<OV>,
  OG extends OptionGroup<OV> = OptionGroup<OV>,
> extends BaseSearchInputProps<OV> {
  value?: O['value'];
  onChange: (option: O, group?: OG) => void;
}

export const SingleValueSearchInput = <
  OV extends OptionValue = OptionValue,
  O extends Option<OV> = Option<OV>,
  OG extends OptionGroup<OV> = OptionGroup<OV>,
>(
  props: SingleValueSearchInputProps<OV, O, OG>,
) => {
  const { options, value, onChange, ...rest } = props;
  const selected =
    filterOptions(options, (option) => option.value === value).at(0) ??
    (value !== undefined ? ({ label: String(value), value } as O) : undefined);
  return <SingleSearchInput {...rest} options={options} value={selected} onChange={onChange} />;
};

/**
 * Search input for multi value selection.
 * - Output is an array of selected options.
 * - Input value is an array of the values of the selected options (options are assumed to have UNIQUE values across all groups)
 *
 * Use when you need to pick multiple items from a flat list of unique options.
 */

interface MultiValueSearchInputProps<OV extends OptionValue, O extends Option<OV> = Option<OV>>
  extends BaseSearchInputProps<OV> {
  value: O['value'][];
  onChange: (options: O[]) => void;
}

export const MultiValueSearchInput = <
  OV extends OptionValue = OptionValue,
  O extends Option<OV> = Option<OV>,
>(
  props: MultiValueSearchInputProps<OV, O>,
) => {
  const { options, value, onChange, ...rest } = props;
  const found = filterOptions(options, (option) => value.includes(option.value));
  const foundValues = new Set(flattenSelectOptions(found).map((o) => o.value));
  const missing = value
    .filter((v) => !foundValues.has(v))
    .map((v) => ({ label: String(v), value: v }) as O);
  const selected = [...missing, ...found];
  return (
    <MultiSearchInput
      {...rest}
      options={options}
      value={selected}
      onChange={(selectedOptions) => onChange(flattenSelectOptions(selectedOptions) as O[])}
    />
  );
};

/**
 * Base component. Supports all features but with less convenient props.
 */

export interface SearchInputProps<OV extends OptionValue = OptionValue>
  extends BaseSearchInputProps<OV> {
  value?: SelectOption<OV> | SelectOption<OV>[];
  onChange: (value: SelectOption<OV> | SelectOption<OV>[]) => void;
}

export const SearchInput = <OV extends OptionValue = OptionValue>(props: SearchInputProps<OV>) => {
  const {
    options,
    value,
    autoFocus,
    placeholder = 'Search...',
    label,
    onChange,
    size = 'small',
    disabled = false,
    loading = false,
    closeOnSelect = true,
    showShortcuts = false,
    fullWidth = true,
    allowCustomValue = false,
    theme,
    customValueHint,
    customValueLabel,
  } = props;

  const multiple = Array.isArray(value);

  const valueArray = useMemo<SelectOptions<OV>>(
    () => (value === undefined ? [] : Array.isArray(value) ? value : [value]),
    [value],
  );

  const handleChange = useCallback(
    (selection: SelectOptions<OV>) => {
      onChange(selection);
    },
    [onChange],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const triggerRect = useClientRect(triggerRef, [isOpen]);

  const position = {
    top: (triggerRect?.top ?? 0) + (visualViewport?.offsetTop ?? 0), // Adjust for mobile browsers
    left: triggerRect?.left ?? 0,
  };

  const selectedOptionLabel = useMemo(
    () => getSelectedOptionLabel(valueArray, multiple),
    [valueArray, multiple],
  );

  const closePanel = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, [triggerRef]);

  useEffect(() => {
    if (autoFocus === true) {
      triggerRef.current?.focus();
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={classNames(styles.searchInput, props.className, {
        [styles.sizeSmall]: size === 'small',
        [styles.sizeMedium]: size === 'regular',
        [styles.sizeLarge]: size === 'large',
        [styles.fullWidth]: fullWidth,
        [styles.themeLight]: theme === 'light',
      })}>
      <SearchInputTrigger
        label={loading ? 'Loading...' : (label ?? selectedOptionLabel ?? placeholder ?? '')}
        onOpenPanel={(searchTerm) => {
          setSearchTerm(searchTerm ?? '');
          setIsOpen(!isOpen);
        }}
        ref={triggerRef}
        disabled={disabled || loading}
      />
      {isOpen && (
        <Overlay>
          <div
            className={styles.backdrop}
            onClick={(e) => {
              e.stopPropagation();
              closePanel();
            }}
          />
          <SearchInputPanel<OV>
            searchTerm={searchTerm}
            options={options}
            value={valueArray}
            multiple={multiple}
            position={position}
            placeholder={placeholder}
            width={triggerRect?.width ?? 0}
            showShortcuts={showShortcuts && multiple}
            onChangeSearchTerm={setSearchTerm}
            allowCustomValue={allowCustomValue}
            customValueHint={customValueHint}
            customValueLabel={customValueLabel}
            onChange={(value) => {
              handleChange(value);
              closeOnSelect && closePanel();
            }}
            onClose={closePanel}
            size={size}
          />
        </Overlay>
      )}
    </div>
  );
};

type SearchInputTriggerProps = {
  ref?: React.Ref<HTMLButtonElement>;
  label: string;
  disabled?: boolean;
  onOpenPanel(searchTerm?: string): void;
};

function SearchInputTrigger({
  ref,
  label,
  disabled = false,
  onOpenPanel,
}: SearchInputTriggerProps) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        onOpenPanel();
      }
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        onOpenPanel(e.key);
      }
    },
    [onOpenPanel],
  );

  return (
    <button
      className={styles.trigger}
      type="button"
      onClick={() => onOpenPanel()}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      ref={ref}
      title={label}>
      <span className={styles.label}>{label}</span>
      <div className={styles.chevron}>
        <svg
          focusable="false"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true">
          <path d="M8 11L3 6 3.7 5.3 8 9.6 12.3 5.3 13 6z" />
        </svg>
      </div>
    </button>
  );
}

interface SearchInputPanelProps<OV extends OptionValue> {
  options: SelectOptions<OV> | Readonly<SelectOptions<OV>>;
  searchTerm: string;
  value: SelectOptions<OV> | undefined;
  multiple: boolean;
  position: { top: number; left: number };
  width: number;
  placeholder?: string;
  onChangeSearchTerm: (value: string) => void;
  onChange: (selectedOption: SelectOptions<OV>) => void;
  onClose?: () => void;
  size?: FormInputSize;
  showShortcuts: boolean;
  allowCustomValue: boolean;
  customValueHint?: string;
  customValueLabel?: string;
}

const SearchInputPanel = <OV extends OptionValue>(props: SearchInputPanelProps<OV>) => {
  const {
    options,
    value,
    multiple,
    position,
    width,
    placeholder,
    searchTerm,
    onChangeSearchTerm,
    onChange,
    onClose,
    size,
    showShortcuts,
    allowCustomValue,
    customValueHint,
    customValueLabel,
  } = props;

  const selectedKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const opt of value ?? []) {
      if (isOptionGroup(opt)) {
        for (const o of opt.options) {
          keys.add(makeOptionKey(o.value, opt));
        }
      } else {
        keys.add(makeOptionKey(opt.value));
      }
    }
    return keys;
  }, [value]);

  const [paddedOptions, setPaddedOptions] = useState<SelectOption<OV>[]>(() => {
    return padSelectOptions(options, value ?? []);
  });

  const flattenedKeyedOptions = useMemo((): KeyedOption<OV>[] => {
    return paddedOptions.flatMap((opt) => {
      if (isOptionGroup(opt)) {
        return opt.options.map(
          (o): KeyedOption<OV> => ({
            ...o,
            _key: makeOptionKey(o.value, opt),
          }),
        );
      }
      return [{ ...opt, _key: makeOptionKey(opt.value) }];
    });
  }, [paddedOptions]);

  const listRef = useRef<Array<HTMLElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const filteredKeys = useFuzzySearchList<KeyedOption<OV>, string>({
    strategy: 'smart',
    list: flattenedKeyedOptions,
    queryText: searchTerm.trim(),
    getText: (option) => [option.label, option.value.toString()],
    mapResultItem: ({ item }) => item._key,
  });

  const filteredKeySet = useMemo(() => new Set(filteredKeys), [filteredKeys]);

  const matchCount = filteredKeys.length;
  const hasExactMatch = useMemo(
    () =>
      flattenedKeyedOptions.some(
        (option) => option.label.toLowerCase() === searchTerm.trim().toLowerCase(),
      ),
    [flattenedKeyedOptions, searchTerm],
  );

  const firstSelectedValueIndex = flattenedKeyedOptions
    .filter((o) => filteredKeySet.has(o._key))
    .findIndex((o) => selectedKeys.has(o._key));

  const { refs, context } = useFloating({
    open: true,
  });
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    virtual: true,
    loop: true,
    selectedIndex: searchTerm ? activeIndex : firstSelectedValueIndex,
    onNavigate: (index) => setActiveIndex(index),
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([listNavigation]);

  useEffect(() => {
    setActiveIndex(0);
  }, [searchTerm]);

  const listRect = useClientRect(refs.floating, [searchTerm]);

  const filteredOptions = useMemo(() => {
    return paddedOptions
      .filter((option) => {
        return isOptionGroup(option)
          ? option.options.some((o) => filteredKeySet.has(makeOptionKey(o.value, option)))
          : filteredKeySet.has(makeOptionKey(option.value));
      })
      .map((option) => {
        if (isOptionGroup(option)) {
          return {
            ...option,
            options: option.options.filter((o) =>
              filteredKeySet.has(makeOptionKey(o.value, option)),
            ),
          };
        }
        return option;
      });
  }, [paddedOptions, filteredKeySet]);

  const [direction, setDirection] = useState<'up' | 'down'>('down');
  useEffect(() => {
    direction === 'down' &&
      listRect !== null &&
      listRect.top + listRect.height > window.innerHeight &&
      setDirection('up');
  }, [direction, listRect]);

  const handleSelectOption = useCallback(
    (selection: SelectOptions<OV>) => {
      if (!multiple) {
        onChange(selection);
        return;
      }
      onChange(toggleSelectOptions(value ?? [], selection));
    },
    [multiple, onChange, value],
  );

  const handleSelectAll = useCallback(() => {
    onChange(paddedOptions);
  }, [onChange, paddedOptions]);

  const handleSelectNone = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const handleCustomOption = useCallback(() => {
    const option = { label: searchTerm, value: searchTerm as OV };
    const nextOptions = [...paddedOptions, option];
    setPaddedOptions(nextOptions);
    if (!multiple) {
      onChange([option]);
      return;
    }
    onChange(toggleSelectOptions(value ?? [], [option]));
  }, [paddedOptions, searchTerm, multiple, value, onChange]);

  const showCustomValueOption = allowCustomValue && searchTerm.trim().length > 0 && !hasExactMatch;

  return (
    <FloatingList elementsRef={listRef}>
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onClose?.();
          }
        }}
        className={classNames(styles.panel, {
          [styles.sizeSmall]: size === 'small',
          [styles.sizeMedium]: size === 'regular',
          [styles.sizeLarge]: size === 'large',
          [styles.openAbove]: direction === 'up',
        })}
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: `${width}px`,
        }}>
        <Input
          size={size}
          value={searchTerm}
          placeholder={placeholder}
          onChange={(e) => onChangeSearchTerm(e.target.value)}
          ref={refs.setReference}
          autoFocus
          {...getReferenceProps({
            onKeyDown: (e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onClose?.();
                return;
              }
              const flatOptions = flattenSelectOptions(filteredOptions);
              const activeOption = flatOptions.at(
                activeIndex !== null && activeIndex !== -1 ? activeIndex : 0,
              );

              const isPlainEnter = e.key === 'Enter' && !e.metaKey && !e.ctrlKey;

              if (isPlainEnter || e.key === 'Tab') {
                if (activeOption !== undefined) {
                  e.preventDefault();
                  const group = findGroupForOption(paddedOptions, activeOption);
                  handleSelectOption(
                    group !== null
                      ? [{ ...group, options: [activeOption] } as SelectOption<OV>]
                      : [activeOption],
                  );
                } else if (showCustomValueOption && activeIndex === flatOptions.length) {
                  e.preventDefault();
                  handleSelectOption([{ label: searchTerm, value: searchTerm as OV }]);
                }
              }

              if (
                allowCustomValue &&
                isPlainEnter &&
                activeOption === undefined &&
                searchTerm.trim().length > 0 &&
                !hasExactMatch
              ) {
                e.preventDefault();
                handleCustomOption();
              }
            },
          })}
        />
        <div ref={refs.setFloating} className={styles.items} role="listbox" {...getFloatingProps()}>
          {showShortcuts && (
            <div className={styles.shortcuts}>
              Select:
              <button className={styles.shortcut} onClick={handleSelectAll}>
                All
              </button>{' '}
              /
              <button className={styles.shortcut} onClick={handleSelectNone}>
                None
              </button>
            </div>
          )}

          {matchCount === 0 && !allowCustomValue ? (
            <div className={styles.empty} role="listbox">
              No matches found
            </div>
          ) : (
            <ItemList
              options={filteredOptions}
              onSelectItem={handleSelectOption}
              getItemProps={getItemProps}
              activeIndex={activeIndex}
              isOptionSelected={(option, group) => {
                return selectedKeys.has(makeOptionKey(option.value, group));
              }}
            />
          )}
          {showCustomValueOption && (
            <CustomValueItem
              searchTerm={searchTerm}
              activeIndex={activeIndex}
              getItemProps={getItemProps}
              selected={selectedKeys.has(makeOptionKey(searchTerm as OV))}
              onSelectItem={handleCustomOption}
              label={customValueLabel}
            />
          )}
          {allowCustomValue && !showCustomValueOption && customValueHint !== undefined && (
            <div className={styles.hint}>{customValueHint}</div>
          )}
        </div>
      </div>
    </FloatingList>
  );
};

type ItemListProps<OV extends OptionValue> = {
  options: SelectOption<OV>[];
  isOptionSelected: (option: Option<OV>, group?: OptionGroup<OV>) => boolean;
  activeIndex: number | null;
  onSelectItem: (selection: SelectOptions<OV>) => void;
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
};

const ItemList = <OV extends OptionValue>(props: ItemListProps<OV>) => {
  const { options } = props;

  return (
    <>
      {options.map((optionOrGroup, i) => {
        if (isOptionGroup(optionOrGroup)) {
          if (optionOrGroup.options.length === 0) {
            return null;
          }
          return (
            <Fragment key={i}>
              <div className={styles.title}>{optionOrGroup.label}</div>
              <ItemList
                options={optionOrGroup.options}
                onSelectItem={(subSelection) =>
                  props.onSelectItem([{ ...optionOrGroup, options: subSelection as Option<OV>[] }])
                }
                activeIndex={props.activeIndex}
                getItemProps={props.getItemProps}
                isOptionSelected={(o) => props.isOptionSelected(o, optionOrGroup)}
              />
            </Fragment>
          );
        }
        return (
          <Item
            key={i}
            option={optionOrGroup}
            onSelectItem={props.onSelectItem}
            selected={props.isOptionSelected(optionOrGroup)}
            activeIndex={props.activeIndex}
            getItemProps={props.getItemProps}
          />
        );
      })}
    </>
  );
};

type ItemProps<OV extends OptionValue> = {
  option: Option<OV>;
  selected?: boolean;
  activeIndex: number | null;
  onSelectItem: (selection: SelectOptions<OV>) => void;
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
};

const Item = <OV extends OptionValue>(props: ItemProps<OV>) => {
  const { option, activeIndex, selected = false } = props;
  const { ref, index } = useListItem();

  const buttonElem = (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      title={'title' in option ? option.title : option.label}
      className={classNames(styles.item, {
        [styles.selected]: selected,
        [styles.active]: index === activeIndex && activeIndex !== -1,
      })}
      disabled={option.disabled}
      {...props.getItemProps({
        onClick: () => props.onSelectItem([option]),
      })}>
      {option.icon !== undefined && <Icon size={16} name={option.icon} />}
      <span className={styles.label}>{option.label}</span>
      {selected && <Icon size={16} name="Check" className={styles.check} />}
    </button>
  );

  return (
    <div>
      {option.tooltip === undefined ? (
        <div ref={ref}>{buttonElem}</div>
      ) : (
        <Tooltip
          ref={ref}
          placement="left"
          color="light"
          hasArrow={false}
          delay={0}
          content={option.tooltip}
          open={activeIndex === index}
          trigger={null}
          hideIfReferenceHidden>
          {buttonElem}
        </Tooltip>
      )}
    </div>
  );
};

interface CustomValueItemProps {
  searchTerm: string;
  activeIndex: number | null;
  selected: boolean;
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
  onSelectItem: () => void;
  label?: string;
}

const CustomValueItem = (props: CustomValueItemProps) => {
  const { searchTerm, activeIndex, getItemProps, onSelectItem, selected, label } = props;
  const { ref, index } = useListItem();

  return (
    <div ref={ref}>
      <button
        type="button"
        role="option"
        aria-selected={selected}
        className={classNames(styles.item, {
          [styles.selected]: selected,
          [styles.active]: index === activeIndex && activeIndex !== -1,
        })}
        {...getItemProps({ onClick: onSelectItem })}>
        <span className={styles.label}>
          {label !== undefined ? `${label}: "${searchTerm}"` : `Use custom value: "${searchTerm}"`}
        </span>
        {selected && <Icon size={16} name="Check" className={styles.check} />}
      </button>
    </div>
  );
};
